import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import TransactionDetailsAdjustmentWalletPage from './TransactionDetailsAdjustmentWallet/TransactionDetailsAdjustmentWalletPage'
import TransactionDetailsBonusPage from './TransactionDetailsBonus/TransactionDetailsBonusPage'
import TransactionDetailsIBCashbackPage from './TransactionDetailsIBCashback/TransactionDetailsIBCashbackPage'
import TransactionDetailsIBCommissionsPage from './TransactionDetailsIBCommissions/TransactionDetailsIBCommissionsPage'
import TransactionDetailsPlatformTransferPage from './TransactionDetailsPlatformTransfer/TransactionDetailsPlatformTransferPage'
import TransactionDetailsWalletDepositWithdrawalPage from './TransactionDetailsWalletDepositWithdrawal/TransactionDetailsWalletDepositWithdrawalPage'

export enum TransactionDetailsView {
  WalletTransfer = 100,
  DepositFromPaymentAgent = 112,
  WithdrawToPaymentAgent = 114,
  WalletDeposit = 200,
  WalletWithdrawal = 300,
  TransferFromTradingAccount = 400,
  InactivityTransfer = 450,
  TransferToTradingAccount = 500,
  TradingAccountBalanceConversion = 520,
  ManualAdjustment = 610,
  Refund = 630,
  WelcomeAccountReward = 640,
  TradingIssueCompensation = 650,
  Bonus = 660,
  MarginBonusIn = 661,
  MarginBonusOut = 663,
  InterEntityTransfer = 662,
  UnallocatedFundsTransfer = 664,
  ArchivedAccountTransfer = 668,
  IBCommission = 670,
  MIBCommission = 675,
  IBMultiTier = 676,
  Reverse = 680,
  IBCashback = 682,
  IBCashbackAdjustment = 683,
  BankFeesRefund = 685,
  DeactivatedAccountWalletTransfer = 686,
  FeeDeduction = 688,
  CopyTraderFees = 690,
  LoyaltyRewardPayout = 695,
  MonthlyInterestTransfer = 696,
  InactivityFee = 698,
  NegativeEquityRefund = 710,
  ZuluTradePerformanceFee = 720,
  ZuluTradeSubscriptionFee = 730,
  ManualTradingAccountAdjustment = 740,
  CampaignBonus = 750,
  PelicanPerformanceFee = 764,
  SocialTradingCampaign = 765,
  PelicanMonthlyFee = 768,
  SubscriptionFee = 770,
  CopyTraderFeesTA = 775,
  DividendAdjustment = 780,
  PerpetualsFunding = 781,
  SQFFinanceAdjustments = 782,
  RolloverAdjustment = 790,
  DividendAdjustmentIndex = 791,
  SocialTradingPerformanceFee = 795,
  SocialTradingManagementFee = 796,
  SocialTradingRegistrationFee = 797,
  SocialTradingPlatformFee = 798,
  SocialTradingAgentFee = 799,
  WithdrawBulk = 2100,
}

const ResolveTransactionDetailsRoute = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const { id } = useParams<{ id: string }>()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    if (id) return walletsApiClient.getTransactionById(id)
    return Promise.resolve(null)
  }, [id])
  const { data: transaction, isLoading } = useFetchOne(callback)

  const innerRender = useMemo(() => {
    if (isLoading) {
      return <Spinner />
    }
    if (!transaction) {
      return <PageNotFound />
    }
    if (transaction.type.id === TransactionDetailsView.WalletTransfer) {
      return (
        <React.Fragment>
          <TransactionDetailsAdjustmentWalletPage data={transaction} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (
      transaction.type.id === TransactionDetailsView.DepositFromPaymentAgent ||
      transaction.type.id === TransactionDetailsView.WithdrawToPaymentAgent
    ) {
      return (
        <React.Fragment>
          <TransactionDetailsAdjustmentWalletPage data={transaction} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (
      transaction.type.id === TransactionDetailsView.WalletDeposit ||
      transaction.type.id === TransactionDetailsView.WalletWithdrawal
    ) {
      return (
        <React.Fragment>
          <TransactionDetailsWalletDepositWithdrawalPage
            data={transaction}
            forceRefresh={forceRefresh}
          />
        </React.Fragment>
      )
    }
    if (
      transaction.type.id === TransactionDetailsView.TransferFromTradingAccount ||
      transaction.type.id === TransactionDetailsView.InactivityTransfer ||
      transaction.type.id === TransactionDetailsView.TransferToTradingAccount
    ) {
      return (
        <React.Fragment>
          <TransactionDetailsPlatformTransferPage data={transaction} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (
      transaction.type.id === TransactionDetailsView.ManualAdjustment ||
      transaction.type.id === TransactionDetailsView.Refund ||
      transaction.type.id === TransactionDetailsView.WelcomeAccountReward ||
      transaction.type.id === TransactionDetailsView.TradingIssueCompensation ||
      transaction.type.id === TransactionDetailsView.ArchivedAccountTransfer ||
      transaction.type.id === TransactionDetailsView.UnallocatedFundsTransfer ||
      transaction.type.id === TransactionDetailsView.InterEntityTransfer
    ) {
      return (
        <React.Fragment>
          <TransactionDetailsAdjustmentWalletPage data={transaction} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (
      transaction.type.id === TransactionDetailsView.Bonus ||
      transaction.type.id === TransactionDetailsView.MarginBonusIn ||
      transaction.type.id === TransactionDetailsView.MarginBonusOut
    ) {
      return (
        <React.Fragment>
          <TransactionDetailsBonusPage data={transaction} />
        </React.Fragment>
      )
    }
    if (
      transaction.type.id === TransactionDetailsView.IBCommission ||
      transaction.type.id === TransactionDetailsView.MIBCommission ||
      transaction.type.id === TransactionDetailsView.IBMultiTier
    ) {
      return <TransactionDetailsIBCommissionsPage data={transaction} forceRefresh={forceRefresh} />
    }
    if (transaction.type.id === TransactionDetailsView.IBCashback) {
      return <TransactionDetailsIBCashbackPage data={transaction} forceRefresh={forceRefresh} />
    }
    if (
      transaction.type.id === TransactionDetailsView.Reverse ||
      transaction.type.id === TransactionDetailsView.IBCashback ||
      transaction.type.id === TransactionDetailsView.IBCashbackAdjustment ||
      transaction.type.id === TransactionDetailsView.BankFeesRefund ||
      transaction.type.id === TransactionDetailsView.DeactivatedAccountWalletTransfer ||
      transaction.type.id === TransactionDetailsView.FeeDeduction ||
      transaction.type.id === TransactionDetailsView.ManualTradingAccountAdjustment ||
      transaction.type.id === TransactionDetailsView.SocialTradingCampaign ||
      transaction.type.id === TransactionDetailsView.ZuluTradePerformanceFee ||
      transaction.type.id === TransactionDetailsView.ZuluTradeSubscriptionFee ||
      transaction.type.id === TransactionDetailsView.NegativeEquityRefund ||
      transaction.type.id === TransactionDetailsView.PelicanMonthlyFee ||
      transaction.type.id === TransactionDetailsView.PelicanPerformanceFee ||
      transaction.type.id === TransactionDetailsView.CampaignBonus ||
      transaction.type.id === TransactionDetailsView.TradingAccountBalanceConversion ||
      transaction.type.id === TransactionDetailsView.SubscriptionFee ||
      transaction.type.id === TransactionDetailsView.PerpetualsFunding ||
      transaction.type.id === TransactionDetailsView.CopyTraderFeesTA ||
      transaction.type.id === TransactionDetailsView.DividendAdjustment ||
      transaction.type.id === TransactionDetailsView.MonthlyInterestTransfer ||
      transaction.type.id === TransactionDetailsView.InactivityFee ||
      transaction.type.id === TransactionDetailsView.CopyTraderFees ||
      transaction.type.id === TransactionDetailsView.DividendAdjustmentIndex ||
      transaction.type.id === TransactionDetailsView.LoyaltyRewardPayout ||
      transaction.type.id === TransactionDetailsView.RolloverAdjustment ||
      transaction.type.id === TransactionDetailsView.SocialTradingPerformanceFee ||
      transaction.type.id === TransactionDetailsView.SocialTradingManagementFee ||
      transaction.type.id === TransactionDetailsView.SocialTradingRegistrationFee ||
      transaction.type.id === TransactionDetailsView.SocialTradingPlatformFee ||
      transaction.type.id === TransactionDetailsView.SocialTradingAgentFee ||
      transaction.type.id === TransactionDetailsView.SQFFinanceAdjustments
    ) {
      return (
        <TransactionDetailsAdjustmentWalletPage data={transaction} forceRefresh={forceRefresh} />
      )
    }
    return <PageNotFound />
  }, [transaction])

  return <Loading isLoading={isLoading}>{innerRender}</Loading>
}

export default ResolveTransactionDetailsRoute
